import { Box, Container, Grid, Typography, useTheme } from "@mui/material";
import { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Anchors, routes } from "../../constants/routes";
import { GoogleServicesContext } from "../../context/GoogleServicesContext";
import siteImages from "../../data/siteImages";
import { gaAction, gaCategory } from "../../utils/analytics";
import { useGradient } from "../../utils/useGradient";
import { replaceParam, smoothScroll } from "../../utils/utilities";
import Button, { ButtonVariant } from "../utils/Button";
import CookieConsent from "../utils/CookieConsent";
import NavigationPanel from "../utils/NavigationPanel";
import SocialButton from "../utils/SocialButton";
import SwitchModeButtons from "../utils/SwitchModeButtons";
import Logo from "./Logo";

const Footer = () => {
  const { t } = useTranslation();
  const { analyticsEvent } = useContext(GoogleServicesContext);
  const anchorToNavigateRef = useRef<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const gradient = useGradient("horizontal");

  useEffect(() => {
    if (anchorToNavigateRef.current) {
      smoothScroll(anchorToNavigateRef.current);
      anchorToNavigateRef.current = null;
    }
  }, [location]);

  const navigateHomeWithScroll = (anchor: string) => {
    analyticsEvent(gaCategory.aboutUs, gaAction.click, anchor);
    anchorToNavigateRef.current = anchor;
    navigate(routes.home);
  };

  return (
    <footer style={{ flexShrink: 0 }}>
      <Container
        maxWidth="xl"
        sx={{
          width: "100%",
          p: { xs: 8, md: 10 },
          mt: { xs: 6, md: 8 },
          color: "white",
          background: gradient,
          borderTopLeftRadius: { xs: 70, md: 100 },
          borderTopRightRadius: { xs: 20, md: 30 }
        }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 5
          }}>
          <Link
            to={routes.home}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            }}>
            <Logo variant="footer" />
          </Link>
        </Box>

        <Grid container spacing={8}>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            }}>
            <Typography variant="h5" fontWeight="bold" mb={5}>
              {t("footer.navigation")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2
              }}>
              <Button
                variant={ButtonVariant.accent}
                label={t("buttons.news")}
                onClick={() => navigateHomeWithScroll(Anchors.news)}
                sx={{
                  width: theme.spacing(40)
                }}
              />
              <Button
                variant={ButtonVariant.accent}
                label={t("buttons.preparing")}
                onClick={() => navigateHomeWithScroll(Anchors.preparing)}
                sx={{
                  width: theme.spacing(40)
                }}
              />
              <Button
                variant={ButtonVariant.accent}
                label={t("buttons.aboutUs")}
                onClick={() => navigateHomeWithScroll(Anchors.aboutUs)}
                sx={{
                  width: theme.spacing(40)
                }}
              />
              <Button
                variant={ButtonVariant.accent}
                label={t("buttons.showroom")}
                onClick={() => navigateHomeWithScroll(Anchors.showroom)}
                sx={{
                  width: theme.spacing(40)
                }}
              />
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            }}>
            <nav aria-label={t("footer.sections")}>
              <Typography variant="h5" fontWeight="bold" mb={5}>
                {t("footer.sections")}
              </Typography>
              <NavigationPanel variant="footer" />
            </nav>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            gap={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            }}>
            <Typography variant="h5" fontWeight="bold">
              {t("footer.followUs")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2
              }}>
              {siteImages.facebook && (
                <SocialButton
                  network="facebook"
                  link={t("footer.facebook")}
                  logoSrc={siteImages.facebook.src}
                  logoAlt={siteImages.facebook.alt}
                />
              )}
              {siteImages.instagram && (
                <SocialButton
                  network="instagram"
                  link={t("footer.instagram")}
                  logoSrc={siteImages.instagram.src}
                  logoAlt={siteImages.instagram.alt}
                />
              )}
            </Box>
            <SwitchModeButtons />
          </Grid>
        </Grid>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            mt: 10
          }}>
          <CookieConsent />
          <Typography sx={{ mt: 3 }}>
            {replaceParam(
              t("footer.disclaimer"),
              "currentYear",
              new Date().getFullYear().toString()
            )}
          </Typography>
        </Box>
      </Container>
    </footer>
  );
};

export default Footer;
